/**
 * Value Rules Engine
 * When conditions on source fields match, sets target field values.
 */

import { ConditionEvaluator } from './condition_evaluator.js';
import { FormatUtils } from '../utils/format_utils.js';

export class ValueRulesEngine {
  constructor(options = {}) {
    this.rules = [];
    this.listeners = [];
    this.formConfig = null;
    this.container = options.container || null;
    this.onAfterSetSelect = options.onAfterSetSelect || (() => {});
  }

  /**
   * Compute date for today + offset (days, weeks, months, years). Amount can be negative.
   * @param {number} amount
   * @param {string} unit - 'days' | 'weeks' | 'months' | 'years'
   * @returns {string} YYYY-MM-DD
   */
  static computeTodayOffset(amount, unit) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const n = Number(amount) || 0;
    switch (unit) {
      case 'days':
        d.setDate(d.getDate() + n);
        break;
      case 'weeks':
        d.setDate(d.getDate() + n * 7);
        break;
      case 'months':
        d.setMonth(d.getMonth() + n);
        break;
      case 'years':
        d.setFullYear(d.getFullYear() + n);
        break;
      default:
        return FormatUtils.getTodayDate();
    }
    return FormatUtils.formatDate(d, 'YYYY-MM-DD');
  }

  /**
   * Collect all source field IDs from a rule's conditions (same as ConditionEngine)
   * @param {Array} conditions - Condition blocks
   * @returns {Set<string>}
   */
  static getSourceFieldIds(conditions) {
    const fieldIds = new Set();
    if (!conditions || !Array.isArray(conditions)) return fieldIds;
    conditions.forEach(block => {
      if (block.terms && Array.isArray(block.terms)) {
        block.terms.forEach(term => {
          if (term.field) {
            let fieldId = term.field;
            if (fieldId.indexOf('FUNC_') > -1) {
              const parts = fieldId.split('_');
              fieldId = parts[2] || fieldId;
            }
            fieldIds.add(fieldId);
          }
        });
      }
    });
    return fieldIds;
  }

  /**
   * Resolve the value to set for a rule (handles date setValueDate).
   * @param {Object} rule - { setValue, setValueDate?, targetFieldId }
   * @param {string} targetFieldType - e.g. 'date', 'input', 'select'
   * @returns {string}
   */
  resolveSetValue(rule, targetFieldType) {
    if (rule.setToEmpty) return '';
    if (targetFieldType === 'date' && rule.setValueDate && typeof rule.setValueDate === 'object') {
      const d = rule.setValueDate;
      if (d.type === 'today') return FormatUtils.getTodayDate();
      if (d.type === 'custom' && d.value) return String(d.value).trim();
      if (d.type === 'todayOffset' && d.unit != null && d.amount != null) {
        return ValueRulesEngine.computeTodayOffset(d.amount, d.unit);
      }
    }
    return rule.setValue != null ? String(rule.setValue).trim() : '';
  }

  /**
   * Setup value rules: attach listeners to source fields, evaluate and set targets when they change
   * @param {Array} rules - Array of { targetFieldId, setValue, setValueDate?, conditions }
   * @param {Object} options - Optional { formConfig, container, onAfterSetSelect }
   */
  setupValueRules(rules, options = {}) {
    this.cleanup();
    if (!rules || !Array.isArray(rules) || rules.length === 0) return;

    this.rules = rules;
    this.formConfig = options.formConfig || null;
    if (options.container != null) {
      this.container = options.container;
    }
    if (options.onAfterSetSelect) {
      this.onAfterSetSelect = options.onAfterSetSelect;
    }

    const allSourceIds = new Set();
    this.rules.forEach(rule => {
      const ids = ValueRulesEngine.getSourceFieldIds(rule.conditions);
      ids.forEach(id => allSourceIds.add(id));
    });

    // Use delegation: one listener on the container so any field change (including in dynamically added repeater rows) triggers re-eval.
    // Skip programmatic events (e.g. from setFieldValue -> updateNiceSelect -> change) to avoid infinite loop.
    const scope = this.container || document;
    const handler = (e) => {
      if (e && e.isTrusted === false) return;
      this.evaluateValueRules();
    };
    const useCapture = true;
    if (scope) {
      scope.addEventListener('input', handler, useCapture);
      scope.addEventListener('change', handler, useCapture);
      this.listeners.push({ field: scope, handler, event: 'input', capture: useCapture });
      this.listeners.push({ field: scope, handler, event: 'change', capture: useCapture });
    }

    this.evaluateValueRules();
  }

  /**
   * Evaluate all rules and set target field values where conditions match.
   * For repeater rows, each row is evaluated and set independently. Non-repeater targets use global evaluation.
   * First matching rule wins per target field (per row for repeaters).
   */
  evaluateValueRules() {
    if (!this.rules || this.rules.length === 0) return;

    const fields = this.formConfig && Array.isArray(this.formConfig.fields) ? this.formConfig.fields : [];
    const scope = this.container || document;

    // Per-row: only repeater area rows (skip non-repeater area's single .colsListWrapper so we don't mix contexts)
    const allRows = scope.querySelectorAll('.colsListWrapper');
    const repeaterRows = Array.from(allRows).filter(row => row.closest('.area[data-repeater="1"]'));

    repeaterRows.forEach((row) => {
      const targetsAlreadySetInRow = new Set();
      this.rules.forEach(rule => {
        if (!rule.conditions || !rule.targetFieldId) return;
        if (targetsAlreadySetInRow.has(rule.targetFieldId)) return;
        // Only run rule if this row contains the target field
        const targetInRow = row.querySelector(
          `[name="${rule.targetFieldId}"], [name^="${rule.targetFieldId}["], [id="${rule.targetFieldId}"]`
        );
        if (!targetInRow) return;
        // Evaluate conditions scoped to this row so we use this row's source field values (e.g. radios)
        const matched = ConditionEvaluator.evaluateBlocks(rule.conditions, row);
        if (matched) {
          const fieldConfig = fields.find(f => f.id === rule.targetFieldId);
          const targetFieldType = fieldConfig ? fieldConfig.type : '';
          const valueToSet = this.resolveSetValue(rule, targetFieldType);
          this.setFieldValue(rule.targetFieldId, valueToSet, row);
          targetsAlreadySetInRow.add(rule.targetFieldId);
        }
      });
    });

    // Non-repeater targets: any target that has no instance inside a .colsListWrapper
    const targetsAlreadySetGlobal = new Set();
    this.rules.forEach(rule => {
      if (!rule.conditions || !rule.targetFieldId) return;
      if (targetsAlreadySetGlobal.has(rule.targetFieldId)) return;
      const elements = scope.querySelectorAll(
        `[name="${rule.targetFieldId}"], [name="${rule.targetFieldId}[]"], [name^="${rule.targetFieldId}["], [id="${rule.targetFieldId}"]`
      );
      const inRepeater = Array.from(elements).some(el => {
        const wrapper = el.closest('.colsListWrapper');
        return wrapper && wrapper.closest('.area[data-repeater="1"]');
      });
      if (inRepeater) return; // already handled per row
      const matched = ConditionEvaluator.evaluateBlocks(rule.conditions, scope);
      if (matched) {
        const fieldConfig = fields.find(f => f.id === rule.targetFieldId);
        const targetFieldType = fieldConfig ? fieldConfig.type : '';
        const valueToSet = this.resolveSetValue(rule, targetFieldType);
        this.setFieldValue(rule.targetFieldId, valueToSet);
        targetsAlreadySetGlobal.add(rule.targetFieldId);
      }
    });
  }

  /**
   * Set a field's value (input, select, textarea, radio, checkbox)
   * @param {string} targetFieldId - Field id/name
   * @param {string} value - Value to set
   * @param {Element|null} [rowScope] - Optional repeater row (.colsListWrapper) to set only the control in this row
   */
  setFieldValue(targetFieldId, value, rowScope = null) {
    const strValue = value == null ? '' : String(value).trim();
    const scope = rowScope || this.container || document;
    let elements = scope.querySelectorAll(
      `[name="${targetFieldId}"], [name="${targetFieldId}[]"], [name^="${targetFieldId}["], [id="${targetFieldId}"]`
    );
    // When scoping to a row, only touch the control that belongs to this row (closest .colsListWrapper must be rowScope)
    if (rowScope && elements.length > 0) {
      elements = Array.from(elements).filter(el => el.closest('.colsListWrapper') === rowScope);
    }
    if (!elements || elements.length === 0) return;

    const first = elements[0];

    if (first.type === 'radio') {
      const toCheck = Array.from(elements).find(
        el => String(el.value || '').toLowerCase() === strValue.toLowerCase()
      );
      elements.forEach(el => { el.checked = el === toCheck; });
      if (first.name) {
        first.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (first.type === 'checkbox') {
      const valuesLower = new Set(
        strValue.split(',').map(s => s.trim()).filter(Boolean).map(s => s.toLowerCase())
      );
      elements.forEach(el => {
        const optionVal = String(el.value || '').toLowerCase();
        el.checked = valuesLower.has(optionVal);
      });
      first.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (first.tagName === 'SELECT') {
      first.value = strValue;
      this.onAfterSetSelect(first);
      setTimeout(() => {
        first.dispatchEvent(new Event('change', { bubbles: true }));
      }, 150);
      return;
    }

    first.value = strValue;
    first.dispatchEvent(new Event('input', { bubbles: true }));
    first.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Remove all listeners
   */
  cleanup() {
    this.listeners.forEach(({ field, handler, event, capture }) => {
      if (field && handler) {
        field.removeEventListener(event, handler, !!capture);
      }
    });
    this.listeners = [];
    this.rules = [];
  }
}
